import { defineMessages, useIntl } from 'react-intl';

import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { SPACE_LOOK_AND_FEEL_SETTINGS } from '@confluence/named-routes/entry-points/routes.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';

const SPACE_LOOK_AND_FEEL_WEBITEM_SECTION_ID = '-system.space.tools-lookandfeel';
const LEGACY_THEME_COMPLETEKEY = 'confluence.sections.space.tools.v2:choosetheme';
const LEGACY_TEMPLATES_COMPLETEKEY =
	'com.atlassian.confluence.plugins.confluence-templates:space-templates-v2';
const LEGACY_PAGE_LAYOUT_COMPLETEKEY = 'confluence.sections.space.tools.v2:custompagecontent';
const LEGACY_PDF_EXPORT_COMPLETEKEY = 'com.atlassian.confluence.extra.flyingpdf:configpdf';

const i18n = defineMessages({
	pageLayoutTitle: {
		id: 'space-settings.web-item-section.look-and-feel.web-item.page-layout',
		defaultMessage: 'Layout',
		description: 'Label for the web item link for page layout.',
	},
	pdfExportTitle: {
		id: 'space-settings.web-item-section.look-and-feel.web-item.pdf-export',
		defaultMessage: 'PDF export formatting',
		description: 'Label for the web item link for pdf styling page.',
	},
});

// To use the same modern site settings pages under the space settings "look and feel" section,
// we're introducing new SPA URLs for those pages.
// @see https://hello.atlassian.net/wiki/spaces/CAXP/pages/3926712468/Space+settings+migrations+decisions
export const useMigratedWebItem: SpaceSettingsWebItemSectionsInjector = ({ webItemSections }) => {
	const intl = useIntl();
	const spaceKey = useSpaceKey();

	if (!spaceKey) {
		return webItemSections;
	}

	return webItemSections?.map((section) => {
		if (section?.id === SPACE_LOOK_AND_FEEL_WEBITEM_SECTION_ID) {
			return {
				...section,
				items: section.items.map((item) => {
					if (item?.completeKey === LEGACY_THEME_COMPLETEKEY) {
						return {
							...item,
							url: SPACE_LOOK_AND_FEEL_SETTINGS.toUrl({ spaceKey, tab: 'themes' }),
						};
					}

					if (item?.completeKey === LEGACY_TEMPLATES_COMPLETEKEY) {
						return {
							...item,
							url: SPACE_LOOK_AND_FEEL_SETTINGS.toUrl({ spaceKey, tab: 'templates' }),
						};
					}

					if (item?.completeKey === LEGACY_PAGE_LAYOUT_COMPLETEKEY) {
						return {
							...item,
							label: intl.formatMessage(i18n.pageLayoutTitle),
							url: SPACE_LOOK_AND_FEEL_SETTINGS.toUrl({ spaceKey, tab: 'page-layout' }),
						};
					}

					if (item?.completeKey === LEGACY_PDF_EXPORT_COMPLETEKEY) {
						return {
							...item,
							...(expValEquals('confluence_pdf_ncs_enabled', 'isEnabled', true) && {
								label: intl.formatMessage(i18n.pdfExportTitle),
							}),
							url: SPACE_LOOK_AND_FEEL_SETTINGS.toUrl({ spaceKey, tab: 'pdf-export' }),
						};
					}

					return item;
				}),
			};
		}

		return section;
	});
};
